import { useState, useRef, useEffect } from "react";
import { C } from "../lib/colors";
import { sendChatMessage } from "../lib/chat";
import { captureLead } from "../lib/leads";
import { SITE } from "../lib/site";

const GREETING = "Hi! I'm the MOAP assistant — ask me anything about pricing, services, or how it works.";

// After this many user messages, surface the "leave your email" prompt.
// Low enough that engaged visitors see it; high enough that one-off "hi"
// chats don't get nagged.
const FOLLOWUP_THRESHOLD = 2;

const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || "").trim());

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const endRef = useRef(null);

  // Follow-up capture form state.
  const [followupOpen, setFollowupOpen] = useState(false);  // form expanded?
  const [followupDone, setFollowupDone] = useState(false);  // already submitted?
  const [fuName, setFuName]   = useState("");
  const [fuEmail, setFuEmail] = useState("");
  const [fuSaving, setFuSaving] = useState(false);
  const [fuError, setFuError] = useState("");

  const userMessageCount = messages.filter((m) => m.role === "user").length;
  const showFollowupCTA = !followupDone && userMessageCount >= FOLLOWUP_THRESHOLD;

  const submitFollowup = async () => {
    if (fuSaving) return;
    if (!isValidEmail(fuEmail)) {
      setFuError("Please enter a valid email so we can reach back.");
      return;
    }
    setFuSaving(true); setFuError("");
    const ok = await captureLead({
      source:  "chat",
      name:    fuName.trim() || null,
      email:   fuEmail.trim().toLowerCase(),
      message: messages.find((m) => m.role === "user")?.content?.slice(0, 500) || null,
      payload: {
        // Strip down to {role, content} so we don't send any future internal fields.
        transcript: messages.map((m) => ({ role: m.role, content: m.content })),
        capturedAt: new Date().toISOString(),
      },
    });
    setFuSaving(false);
    if (ok) {
      setFollowupDone(true);
      setFollowupOpen(false);
    } else {
      setFuError(`Couldn't save right now. Email us at ${SITE.contactEmail} instead.`);
    }
  };

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setError("");
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const reply = await sendChatMessage(next);
      setMessages(m => [...m, { role: "assistant", content: reply }]);
    } catch (err) {
      setError(err.message || "Could not send message.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating bubble */}
      <button onClick={() => setOpen(o => !o)}
        aria-label={open ? "Close chat" : "Open chat"}
        style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 200,
          width: 56, height: 56, borderRadius: "50%",
          background: open ? C.s1 : C.green,
          color: open ? C.text : "#000",
          border: open ? `1px solid ${C.border}` : "none",
          boxShadow: "0 8px 30px rgba(0,0,0,0.45)",
          cursor: "pointer", fontSize: 22,
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all 0.2s",
        }}>
        {open ? "✕" : "💬"}
      </button>

      {/* Chat panel */}
      {open && (
        <div style={{
          position: "fixed", bottom: 92, right: 24, zIndex: 199,
          width: "min(360px, calc(100vw - 32px))",
          height: "min(520px, calc(100vh - 120px))",
          background: C.s1, border: `1px solid ${C.border}`,
          borderRadius: 16, boxShadow: "0 10px 50px rgba(0,0,0,0.6)",
          display: "flex", flexDirection: "column",
          fontFamily: "'Instrument Sans',sans-serif", color: C.text,
          overflow: "hidden", animation: "fadeUp 0.25s ease",
        }}>
          {/* Header */}
          <div style={{
            padding: "14px 18px",
            borderBottom: `1px solid ${C.border}`,
            background: `linear-gradient(135deg, rgba(0,240,160,0.07), rgba(77,170,255,0.04))`,
          }}>
            <div style={{
              fontSize: 9, color: C.green, fontFamily: "'DM Mono',monospace",
              textTransform: "uppercase", letterSpacing: 2, marginBottom: 4,
            }}>✦ Live Chat</div>
            <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 16, fontWeight: 700, color: "#fff" }}>
              How can we help?
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
              AI assistant · usually replies in seconds
            </div>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1, overflowY: "auto",
            padding: "14px", display: "flex", flexDirection: "column", gap: 10,
          }}>
            {messages.length === 0 && (
              <div style={{
                alignSelf: "flex-start", maxWidth: "90%",
                background: "rgba(255,255,255,0.05)", color: C.text,
                padding: "9px 13px", borderRadius: 12, borderBottomLeftRadius: 4,
                fontSize: 13, lineHeight: 1.55,
              }}>{GREETING}</div>
            )}
            {messages.map((m, i) => (
              <div key={i} style={{
                alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                maxWidth: "90%",
                background: m.role === "user" ? C.green : "rgba(255,255,255,0.05)",
                color: m.role === "user" ? "#000" : C.text,
                padding: "9px 13px", borderRadius: 12,
                borderBottomRightRadius: m.role === "user" ? 4 : 12,
                borderBottomLeftRadius: m.role === "user" ? 12 : 4,
                fontSize: 13, lineHeight: 1.55, whiteSpace: "pre-wrap",
              }}>{m.content}</div>
            ))}
            {loading && (
              <div style={{
                alignSelf: "flex-start", display: "flex", gap: 4,
                background: "rgba(255,255,255,0.05)",
                padding: "11px 14px", borderRadius: 12, borderBottomLeftRadius: 4,
              }}>
                {[0, 1, 2].map(i => (
                  <span key={i} style={{
                    width: 6, height: 6, borderRadius: "50%",
                    background: C.muted,
                    animation: `pulse 1s ${i * 0.15}s infinite`,
                  }}/>
                ))}
              </div>
            )}
            {error && (
              <div style={{
                fontSize: 12, color: C.red, textAlign: "center",
                padding: "8px 4px", lineHeight: 1.5,
              }}>⚠ {error}</div>
            )}
            <div ref={endRef}/>
          </div>

          {/* Follow-up email capture — appears after the visitor has sent
              a couple of messages, collapses to a thank-you state once saved. */}
          {followupDone && (
            <div style={{
              borderTop: `1px solid ${C.border}`,
              padding: "10px 14px", fontSize: 12, color: C.green,
              display: "flex", alignItems: "center", gap: 8,
              background: `${C.green}06`,
            }}>
              <span>✓</span>
              Got it — we'll be in touch within 2 business hours.
            </div>
          )}
          {showFollowupCTA && !followupOpen && (
            <button onClick={() => { setFollowupOpen(true); setFuError(""); }}
              style={{
                borderTop: `1px solid ${C.border}`, borderLeft: 0, borderRight: 0, borderBottom: 0,
                padding: "10px 14px", fontSize: 12, fontWeight: 600,
                background: `${C.green}08`, color: C.green, cursor: "pointer",
                fontFamily: "'DM Mono',monospace", textAlign: "left",
                display: "flex", alignItems: "center", gap: 8, transition: "background 0.15s",
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = `${C.green}14`}
              onMouseLeave={(e) => e.currentTarget.style.background = `${C.green}08`}>
              📩 Want a human follow-up? Drop your email →
            </button>
          )}
          {followupOpen && !followupDone && (
            <div style={{
              borderTop: `1px solid ${C.border}`, padding: "12px 14px",
              background: `${C.green}05`,
              display: "flex", flexDirection: "column", gap: 7,
            }}>
              <div style={{ fontSize: 11, color: C.muted, fontFamily: "'DM Mono',monospace",
                textTransform: "uppercase", letterSpacing: 1, marginBottom: 2 }}>
                Leave your email — we'll reply
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <input value={fuName} onChange={(e) => setFuName(e.target.value)}
                  placeholder="Name (optional)" disabled={fuSaving}
                  style={{ flex: 1, minWidth: 0, background: "rgba(255,255,255,0.04)",
                    border: `1px solid ${C.border}`, borderRadius: 7,
                    padding: "7px 10px", color: C.text, fontSize: 12,
                    fontFamily: "'Instrument Sans',sans-serif" }}/>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <input value={fuEmail} onChange={(e) => setFuEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") submitFollowup(); }}
                  placeholder="you@yourbusiness.com" type="email" disabled={fuSaving}
                  style={{ flex: 1, minWidth: 0, background: "rgba(255,255,255,0.04)",
                    border: `1px solid ${C.border}`, borderRadius: 7,
                    padding: "7px 10px", color: C.text, fontSize: 12,
                    fontFamily: "'Instrument Sans',sans-serif" }}/>
                <button onClick={submitFollowup} disabled={fuSaving}
                  style={{ background: fuSaving ? "rgba(255,255,255,0.05)" : C.green,
                    color: fuSaving ? C.muted : "#000", border: "none", borderRadius: 7,
                    padding: "0 14px", fontSize: 11, fontWeight: 700,
                    cursor: fuSaving ? "not-allowed" : "pointer",
                    fontFamily: "'DM Mono',monospace", flexShrink: 0 }}>
                  {fuSaving ? "…" : "Send"}
                </button>
                <button onClick={() => { setFollowupOpen(false); setFuError(""); }}
                  disabled={fuSaving} aria-label="Close follow-up form"
                  style={{ background: "transparent", color: C.muted, border: `1px solid ${C.border}`,
                    borderRadius: 7, padding: "0 10px", fontSize: 11, cursor: "pointer",
                    fontFamily: "'DM Mono',monospace", flexShrink: 0 }}>
                  ✕
                </button>
              </div>
              {fuError && (
                <div style={{ fontSize: 11, color: C.red }}>⚠ {fuError}</div>
              )}
              <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.45 }}>
                We share your chat transcript with our team so we can reply with context.
              </div>
            </div>
          )}

          {/* Input */}
          <div style={{
            padding: "10px 12px", borderTop: `1px solid ${C.border}`,
            display: "flex", gap: 8, background: C.s1,
          }}>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Type a message…"
              disabled={loading}
              style={{
                flex: 1, minWidth: 0,
                background: "rgba(255,255,255,0.04)",
                border: `1px solid ${C.border}`, borderRadius: 9,
                padding: "9px 12px", color: C.text, fontSize: 13,
                fontFamily: "'Instrument Sans',sans-serif",
              }}
            />
            <button
              onClick={send}
              disabled={loading || !input.trim()}
              aria-label="Send message"
              style={{
                background: loading || !input.trim() ? "rgba(255,255,255,0.06)" : C.green,
                color: loading || !input.trim() ? C.muted : "#000",
                border: "none", borderRadius: 9,
                padding: "0 14px", fontSize: 16, fontWeight: 700,
                cursor: loading || !input.trim() ? "not-allowed" : "pointer",
                fontFamily: "'DM Mono',monospace", flexShrink: 0,
              }}
            >↗</button>
          </div>
        </div>
      )}
    </>
  );
};
